import React, { useEffect, useRef, useState } from 'react';
import lottie from 'lottie-web';
import kLoaderAnimation from '../../assets/images/KLoader.json';

const LogoSpinner = ({ message = "Loading...", size = "medium", className = "" }) => {
  const animationRef = useRef(null);
  const animationInstance = useRef(null);
  const isMounted = useRef(true);
  const [animationLoaded, setAnimationLoaded] = useState(false);
  
  // Size configurations
  const containerSize = {
    small: { width: '60px', height: '60px' },
    medium: { width: '80px', height: '80px' },
    large: { width: '120px', height: '120px' }
  }[size] || { width: '80px', height: '80px' };
  
  // Font size based on spinner size
  const fontSize = {
    small: 'fs-6',
    medium: 'fs-5',
    large: 'fs-4'
  }[size] || 'fs-5';
  
  // Initialize Lottie animation
  useEffect(() => {
    // Set mounted flag true at component mount
    isMounted.current = true;
    
    // Create a safe setState function that checks mounted state
    const safeSetState = (callback) => {
      if (isMounted.current) {
        callback();
      }
    };
    
    const initAnimation = () => {
      // Only initialize if the component is still mounted and ref exists
      if (animationRef.current && isMounted.current) {
        try {
          // Destroy any existing animation before creating a new one
          if (animationInstance.current) {
            animationInstance.current.destroy();
          }
          
          // Store the animation instance in ref for cleanup
          animationInstance.current = lottie.loadAnimation({
            container: animationRef.current,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: kLoaderAnimation,
            rendererSettings: {
              progressiveLoad: true,
              preserveAspectRatio: 'xMidYMid meet'
            }
          });
          
          // Add event listeners
          animationInstance.current.addEventListener('DOMLoaded', () => {
            safeSetState(() => setAnimationLoaded(true));
          });
          
          animationInstance.current.addEventListener('error', (error) => {
            console.error('Lottie animation error:', error);
          });
        } catch (error) {
          console.error('Error initializing Lottie animation:', error);
        }
      }
    };
    
    // Use requestAnimationFrame for smoother initialization
    // This helps avoid the component already unmounted warning
    const rafId = requestAnimationFrame(() => {
      // Initialize with a small timeout to avoid race conditions
      const timeoutId = setTimeout(initAnimation, 50);
      return () => clearTimeout(timeoutId);
    });
    
    // Clean up animation and other resources on component unmount
    return () => {
      // Cancel any pending animation frames
      cancelAnimationFrame(rafId);
      
      // Set mounted flag to false immediately to prevent any further state updates
      isMounted.current = false;
      
      // Clean up animation instance
      if (animationInstance.current) {
        try {
          animationInstance.current.destroy();
        } catch (error) {
          console.error('Error destroying Lottie animation:', error);
        }
        animationInstance.current = null;
      }
    };
  }, []);
  
  return (
    <div className={`text-center py-3 ${className}`}>
      <div 
        ref={animationRef} 
        style={{
          ...containerSize,
          margin: '0 auto'
        }}
      />
      {message && (
        <div className={`mt-2 text-muted ${fontSize}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default LogoSpinner; 